import { Inject, Injectable, InjectionToken, Injector, Optional } from '@angular/core';
import { HttpClient, HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ErrorHandlerInterceptor } from './error-handler.interceptor';
import { ApiPrefixInterceptor } from './api-prefix.interceptor';
import { LoaderInterceptor } from './loader.interceptor';
import { HttpResponseInterceptor } from './http-response.interceptor';
import { CachingInterceptor } from './caching-interceptor.service';
import { ActivityInterceptor } from './ActivityInterceptor.service';
import { ManageHttpInterceptor } from './old/managehttp.interceptor';
import { HttpTokenInterceptor } from './http-token.interceptor';
import { AppConfigurationService } from '@shared/services/app.configuration.service';
import { RequestCache } from './request.cache.service';
import { NgForageCache } from 'ngforage';
import { MonitorInterceptor } from './monitor.interceptor';

// HttpClient is declared in a re-exported module, so we have to extend the original module to make it work properly
// (see https://github.com/Microsoft/TypeScript/issues/13897)
declare module '@angular/common/http' {
  // Augment HttpClient with the added configuration methods from HttpService, to allow in-place replacement of
  // HttpClient with HttpService using dependency injection
  export interface HttpClient {
    /**
     * Enables caching for this request.
     * @param forceUpdate Forces request to be made and updates cache entry.
     * @return The new instance.
     */
    cache(forceUpdate?: boolean): HttpClient;

    /**
     * Skips default error handler for this request.
     * @return The new instance.
     */
    skipErrorHandler(): HttpClient;

    /**
     * Do not use API prefix for this request.
     * @return The new instance.
     */
    disableApiPrefix(): HttpClient;

    /**
     * Do not use Loader for this request.
     * @return The new instance.
     */
    disableLoader(): HttpClient;

    /**
     * Do not use HttpCancel for this request.
     * @return The new instance.
     */
    disableHttpCancel(): HttpClient;

    notify(data: any): HttpClient;

    /**
     * Do not use Monitor interceptor for this request.
     * @return The new instance.
     */
    disableMonitor(): HttpClient;

    /**
     * Do not use Error interceptor for this request.
     * @return The new instance.
     */
    disableErrorHandler(): HttpClient;

    setContext(data: any): HttpClient;
  }
}

// From @angular/common/http/src/interceptor: allows to chain interceptors
class HttpInterceptorHandler implements HttpHandler {
  constructor(private next: HttpHandler, private interceptor: HttpInterceptor) {}

  handle(request: HttpRequest<any>): Observable<HttpEvent<any>> {
    return this.interceptor.intercept(request, this.next);
  }
}

/**
 * Allows to override default dynamic interceptors that can be disabled with the HttpService extension.
 * Except for very specific needs, you should better configure these interceptors directly in the constructor below
 * for better readability.
 *
 * For static interceptors that should always be enabled (like ApiPrefixInterceptor), use the standard
 * HTTP_INTERCEPTORS token.
 */
export const HTTP_DYNAMIC_INTERCEPTORS = new InjectionToken<HttpInterceptor>('HTTP_DYNAMIC_INTERCEPTORS');

@Injectable({
  providedIn: 'root',
})
export class HttpService extends HttpClient {
  appConfig: AppConfigurationService;
  requestCache: RequestCache;

  constructor(
    private httpHandler: HttpHandler,
    private injector: Injector,
    @Optional()
    @Inject(HTTP_DYNAMIC_INTERCEPTORS)
    private interceptors: HttpInterceptor[] = [],
    private ngForageCache: NgForageCache
  ) {
    super(httpHandler);
    this.appConfig = new AppConfigurationService();
    this.requestCache = new RequestCache(this.ngForageCache, new AppConfigurationService());
    if (!this.interceptors) {
      // Configure default interceptors that can be disabled here
      this.interceptors = [
        this.injector.get(MonitorInterceptor),

        this.injector.get(HttpTokenInterceptor),
        this.injector.get(HttpResponseInterceptor),
        this.injector.get(LoaderInterceptor),
        // this.injector.get(ManageHttpInterceptor),
        this.injector.get(ApiPrefixInterceptor),
        this.injector.get(ErrorHandlerInterceptor),
      ];
    }
  }

  override notify(data: any): HttpClient {
    const activityInterceptor = this.injector.get(ActivityInterceptor).configure(data);
    return this.insertInterceptor(activityInterceptor);
  }
  override setContext(data: any): HttpClient {
    this.removeInterceptor(ApiPrefixInterceptor);
    const apiPrefixInterceptor = this.injector.get(ApiPrefixInterceptor).configure(data);
    return this.insertInterceptor(apiPrefixInterceptor);
  }
  override cache(forceUpdate: boolean = true): HttpClient {
    // let applicationCache = true;

    // check client level cache
    // const portalCache = this.appConfig.getByIdentifier(Identifiers.PortalCache);
    // if (portalCache) {
    //   const objPortalCache = portalCache;
    //   applicationCache = objPortalCache["Fields"]["enabled"];
    if (forceUpdate) {
      forceUpdate = this.requestCache.clientLevelCache();
    }
    // }

    const cacheInterceptor = this.injector.get(CachingInterceptor).configure({
      update: forceUpdate,
    });
    return this.insertInterceptor(cacheInterceptor);
  }

  override skipErrorHandler(): HttpClient {
    return this.removeInterceptor(ErrorHandlerInterceptor);
  }

  override disableApiPrefix(): HttpClient {
    return this.removeInterceptor(ApiPrefixInterceptor);
  }

  override disableLoader(): HttpClient {
    return this.removeInterceptor(LoaderInterceptor);
  }

  override disableHttpCancel(): HttpClient {
    return this.removeInterceptor(ManageHttpInterceptor);
  }

  override disableMonitor(): HttpClient {
    return this.removeInterceptor(MonitorInterceptor);
  }
  override disableErrorHandler(): HttpClient {
    return this.removeInterceptor(ErrorHandlerInterceptor);
  }
  // Override the original method to wire interceptors when triggering the request.
  override request(method?: any, url?: any, options?: any): any {
    const handler = this.interceptors.reduceRight(
      (next, interceptor) => new HttpInterceptorHandler(next, interceptor),
      this.httpHandler
    );
    return new HttpClient(handler).request(method, url, options);
  }

  private removeInterceptor(interceptorType: Function): HttpService {
    return new HttpService(
      this.httpHandler,
      this.injector,
      this.interceptors.filter((i) => !(i instanceof interceptorType)),
      this.ngForageCache
    );
  }

  private addInterceptor(interceptor: HttpInterceptor): HttpService {
    return new HttpService(
      this.httpHandler,
      this.injector,
      this.interceptors.concat([interceptor]),
      this.ngForageCache
    );
  }

  private insertInterceptor(interceptor: HttpInterceptor): HttpService {
    const intercepts = [...this.interceptors, interceptor];
    return new HttpService(this.httpHandler, this.injector, intercepts, this.ngForageCache);
  }
}
